import * as React from 'react';

import { cn } from '@/design-system/lib/utils';

import { CopyButton } from '../copy-button';

export interface TruncatedAddressProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Full address (or any long identifier) to render. */
  address: string;
  /** Characters preserved at the start. Defaults to 6 (e.g. `0x1234`). */
  headLength?: number;
  /** Characters preserved at the end. Defaults to 4. */
  tailLength?: number;
  /** When true, renders a `CopyButton` after the truncated text. */
  withCopy?: boolean;
  /** Label passed to the embedded `CopyButton`. */
  copyLabel?: string;
  /** Use the canonical monospace style. Defaults to true. */
  monospace?: boolean;
}

const truncate = (address: string, head: number, tail: number): string => {
  if (!address) return '';
  if (address.length <= head + tail + 1) return address;
  return `${address.slice(0, head)}…${address.slice(-tail)}`;
};

const TruncatedAddress = React.forwardRef<HTMLSpanElement, TruncatedAddressProps>(
  (
    {
      address,
      headLength = 6,
      tailLength = 4,
      withCopy = false,
      copyLabel = 'Copy address',
      monospace = true,
      className,
      ...props
    },
    ref,
  ) => {
    const truncated = truncate(address, headLength, tailLength);

    const content = (
      <span ref={ref} title={address} className={cn(monospace && 'tw:font-mono', className)} {...props}>
        {truncated}
      </span>
    );

    if (!withCopy) return content;

    return (
      <span className="tw:inline-flex tw:items-center tw:gap-1">
        {content}
        <CopyButton value={address} label={copyLabel} />
      </span>
    );
  },
);

TruncatedAddress.displayName = 'TruncatedAddress';

export { truncate as truncateAddress, TruncatedAddress };
